import { useState, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Button,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { gwtStyles } from '../../config/theme';

/* ── Wizard panel definition ── */

export interface WizardPanelDef {
  id: string;
  label: string;
  icon?: ReactNode;
  content: ReactNode;
}

/* ── Props ── */

interface WizardDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  panels: WizardPanelDef[];
  /** Default selected panel id */
  defaultPanel?: string;
  /** Dialog width, default 800 */
  width?: number;
  /** Dialog height, default 600 */
  height?: number;
  /** If provided, shows Save/Cancel buttons */
  onSave?: () => void;
  /** Whether save is in progress */
  saving?: boolean;
}

/**
 * GWT-matching wizard dialog — mirrors WizardDialog.java.
 *
 * Layout: HorizontalSplitPanel equivalent
 *   Left (20%): WizardFlipper — vertical list of wizard tabs with icons
 *   Right (80%): Active WizardPanel content
 *
 * CSS from GWT:
 *   .brit-wizard-detail { background-color: AliceBlue; border: 2px inset LightBlue; }
 *   .brit-wizard-active { background-color: LavenderBlush; border: 2px inset LightBlue; }
 */
export const WizardDialog = ({
  open,
  onClose,
  title,
  panels,
  defaultPanel,
  width = 800,
  height = 600,
  onSave,
  saving,
}: WizardDialogProps) => {
  const [activePanelId, setActivePanelId] = useState(defaultPanel ?? panels[0]?.id ?? '');
  const activePanel = panels.find((p) => p.id === activePanelId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width,
          height,
          maxWidth: '95vw',
          maxHeight: '90vh',
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
          overflow: 'hidden',
        },
      }}
    >
      {/* Title bar */}
      <Box
        sx={{
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          backgroundColor: gwtStyles.wizardDetail,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 14 }}>
          {title}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ width: 24, height: 24 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden', flex: 1 }}>
        {/* Left nav — WizardFlipper (20%) */}
        <Box
          sx={{
            width: '20%',
            minWidth: 140,
            maxWidth: 200,
            overflow: 'auto',
            backgroundColor: gwtStyles.wizardDetail,
            borderRight: '2px inset LightBlue',
            flexShrink: 0,
          }}
        >
          <List dense disablePadding>
            {panels.map((panel) => {
              const isActive = panel.id === activePanelId;
              return (
                <ListItemButton
                  key={panel.id}
                  selected={isActive}
                  onClick={() => setActivePanelId(panel.id)}
                  sx={{
                    py: 1,
                    backgroundColor: isActive ? gwtStyles.wizardActive : 'transparent',
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    '&.Mui-selected': {
                      backgroundColor: gwtStyles.wizardActive,
                      border: '2px inset LightBlue',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: gwtStyles.wizardActive,
                    },
                  }}
                >
                  {panel.icon && (
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {panel.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={panel.label}
                    primaryTypographyProps={{ fontSize: 12, fontWeight: isActive ? 600 : 400 }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Right content — active panel (80%) */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: gwtStyles.wizardDetail,
            border: '2px inset LightBlue',
            p: 2,
          }}
        >
          {activePanel?.content ?? (
            <Typography variant="body2" color="text.secondary">
              Select a panel
            </Typography>
          )}
        </Box>
      </DialogContent>

      {onSave && (
        <DialogActions sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onSave}
            disabled={saving}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
